import React, { useEffect, useState, useCallback } from "react";
import apiService from "../services/apiService";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from "recharts";
import {
  Paper,
  Typography,
  Card,
  CardContent,
  Box,
  Grid,
  MenuItem,
  Select,
  FormControl,
  TextField,
  InputLabel,
  Container,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import debounce from "lodash.debounce";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const BLUE_PALETTE = [
  "#127AFD", // bold blue
  "#2BD4FF", // light cyan blue
  "#42C7F2", // sky blue
  "#397EFF", // medium blue
  "#79E2FF", // pastel blue
  "#9EEDFC", // lightest blue
];

const BG_LIGHT = "#F7FBFF"; // Very light blue for background
const TABLE_ALT = "#E8F6FE"; // Subtle blue for alternate table rows
const BORDER = "#12D1F8"; // Lighter blue/cyan for border (removed extra "ff")
const CARD_BG = "#FFFFFF"; // Card background stays white
const TEXT = "#364960ff"; // Strong contrast for main text (removed extra "ff")

const cardColors = {
  total: "#0f8fe0", // sky blue (good as a friendly info color)
  errors: "#4cc4ff", // bold blue for errors, stands out
  critical: "#4bc4f5", // deepest blue for emphasis on critical
  monitored: "#a8d8f5", // calm and positive for monitored (light aqua)
};

const StatCard = ({ title, value, color }) => (
  <Paper
    elevation={2}
    sx={{
      p: 1,
      borderRadius: 2,
      background: color,
      color: "#4a5d79ff",
      height: 60,
      width: 140,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "flex-start",
      border: "none",
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif', // Reset to browser default font
    }}
  >
    <Typography
      variant="subtitle2"
      fontWeight={600}
      fontSize="16px"
      sx={{ fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}
    >
      {title}
    </Typography>
    <Typography
      variant="h5"
      fontWeight={700}
      fontSize="18px"
      sx={{
        color: "#4b556bff",
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      }}
    >
      {value}
    </Typography>
  </Paper>
);

const SimpleTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <Box
        sx={{
          p: 2,
          bgcolor: CARD_BG,
          border: `1px solid ${BORDER}`,
          borderRadius: 1,
          color: TEXT,
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}
        >
          Count: {payload[0].value}
        </Typography>
      </Box>
    );
  }
  return null;
};

const Home = () => {
  const [kpis, setKpis] = useState({
    totalJobs: 0,
    totalJobsErrors: 0,
    criticalErrors: 0,
    jobsMonitored: 0,
  });
  const [jobErrorsByEnv, setJobErrorsByEnv] = useState([]);
  const [errorTypes, setErrorTypes] = useState([]);
  const [jobStatus, setJobStatus] = useState([]);
  const [jobCriticality, setJobCriticality] = useState([]);
  const [criticalJobs, setCriticalJobs] = useState([]);
  const [processTypes, setProcessTypes] = useState([]);
  const [weeklyErrors, setWeeklyErrors] = useState([]);
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const formatDateForDisplay = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }); // e.g., "Nov 25 2025"
  };
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const fetchDashboardData = useCallback(
    debounce(async (appId, date) => {
      try {
        const queryParams = [];
        if (appId) queryParams.push(`app_id=${appId}`);
        if (date) {
          // Format Date object to YYYY-MM-DD
          const formattedDate = date.toISOString().split("T")[0];
          queryParams.push(`reference_date_1=${formattedDate}`);
          queryParams.push(`date_monitored=${formattedDate}`);
        }
        const query = queryParams.length ? `?${queryParams.join("&")}` : "";

        const [
          appData,
          kpisData,
          envData,
          errorTypesData,
          statusData,
          criticalityData,
          criticalJobsData,
          processTypeData,
          weeklyErrorData,
        ] = await Promise.all([
          apiService.get("/applications"),
          apiService.get(`/dashboard/kpis${query}`),
          apiService.get(`/dashboard/joberrors-by-env${query}`),
          apiService.get(`/dashboard/error-types${query}`),
          apiService.get(`/dashboard/job-status${query}`),
          apiService.get(`/dashboard/job-criticality${query}`),
          apiService.get(`/dashboard/critical-jobs${query}`),
          apiService.get(`/dashboard/process_typecount${query}`),
          apiService.get(`/dashboard/weekly-error-count${query}`),
        ]);

        setApplications(Array.isArray(appData) ? appData : []);
        setKpis(kpisData);
        setJobErrorsByEnv(envData);
        setErrorTypes(errorTypesData);
        setJobStatus(statusData);
        setJobCriticality(criticalityData);
        setCriticalJobs(criticalJobsData);
        setProcessTypes(processTypeData);
        setWeeklyErrors(weeklyErrorData);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
        // Ensure applications is always an array even on error
        setApplications([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [],
  );

  useEffect(() => {
    fetchDashboardData(selectedApp, selectedDate);
  }, [selectedApp, selectedDate, fetchDashboardData]);

  if (loading)
    return (
      <Typography
        sx={{
          p: 3,
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          textShadow: "1px 1px 2px rgba(0,0,0,0.15)",
        }}
      >
        Loading...
      </Typography>
    );
  if (error)
    return (
      <Typography
        sx={{
          p: 3,
          color: "red",
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          textShadow: "1px 1px 2px rgba(0,0,0,0.15)",
        }}
      >
        Error: {error}
      </Typography>
    );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: BG_LIGHT,
        p: { xs: 1.5, md: 3 },
      }}
    >
      <Paper
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: 2,
          bgcolor: CARD_BG,
          p: { xs: 2, md: 3 },
          position: "relative",
        }}
      >
        <Grid container spacing={1} sx={{ mb: 1, alignItems: "center" }}>
          <Typography
          variant="h4"
          fontSize="25px"
          fontWeight="600"
          color="#454545"
          sx={{ mb: 0 }}
        >
            Job Monitoring Dashboard
          </Typography>
        </Grid>
        {/* KPI Cards and Filters */}
        <Grid container spacing={1} sx={{ mb: 1, alignItems: "center" }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Jobs"
              value={kpis.totalJobs}
              color={cardColors.total}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Job Errors"
              value={kpis.totalJobsErrors}
              color={cardColors.errors}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Critical Errors"
              value={kpis.criticalErrors}
              color={cardColors.critical}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Jobs Monitored"
              value={kpis.jobsMonitored}
              color={cardColors.monitored}
            />
          </Grid>
          {/* Application Filter */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl
                fullWidth
                size="small"
                variant="outlined"
                sx={{
                  minWidth: 170,
                  maxWidth: 170,
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: BORDER,
                    },
                    "&:hover fieldset": {
                      borderColor: BORDER,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: BORDER,
                    },
                  },
                }}
              >
                <InputLabel
                  id="app-select-label"
                  sx={{
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: "19px",
                    color: "black",
                    textShadow: "1px 1px 2px rgba(0,0,0,0.15)",
                  }}
                  shrink
                >
                  Applications
                </InputLabel>
                <Select
                  labelId="app-select-label"
                  label="Applications"
                  value={selectedApp}
                  onChange={(e) => setSelectedApp(e.target.value)}
                  notched
                  displayEmpty
                  sx={{
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    minWidth: 170,
                    maxWidth: 170,
                    color: "grey",
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        minWidth: 170,
                        maxWidth: 170,
                        fontFamily:
                          '"Roboto", "Helvetica", "Arial", sans-serif',
                      },
                    },
                  }}
                  renderValue={(selected) =>
                    selected === ""
                      ? "All Applications"
                      : (Array.isArray(applications)
                          ? applications.find((a) => a.app_id === selected)
                              ?.name
                          : "") || ""
                  }
                >
                  <MenuItem
                    value=""
                    sx={{
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                      color: TEXT,
                    }}
                  >
                    All Applications
                  </MenuItem>
                  {Array.isArray(applications) &&
                    applications.map((app) => (
                      <MenuItem
                        key={app.app_id}
                        value={app.app_id}
                        sx={{
                          fontFamily:
                            '"Roboto", "Helvetica", "Arial", sans-serif',
                        }}
                      >
                        {app.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                selected={selectedDate ? new Date(selectedDate) : null}
                onChange={(date) => setSelectedDate(date)}
                dateFormat="MMM dd yyyy"
                placeholderText="Select Date"
                isClearable
                portalId="root-portal" 
                popperPlacement="bottom-start" 
                customInput={
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Date Monitored"
                    InputLabelProps={{
                      shrink: true,
                      style: {
                        fontFamily:
                          '"Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: "19px",
                        color: "black",
                        textShadow: "1px 1px 2px rgba(0,0,0,0.15)",
                      },
                    }}
                    inputProps={{
                      style: {
                        fontFamily:
                          '"Roboto", "Helvetica", "Arial", sans-serif',
                        color: "grey",
                      },
                    }}
                    sx={{
                      minWidth: 170,
                      maxWidth: 170,
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: BORDER },
                        "&:hover fieldset": { borderColor: BORDER },
                        "&.Mui-focused fieldset": { borderColor: BORDER },
                      },
                    }}
                  />
                }
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid container spacing={0.5}>
          {/* Job Errors by Environment */}
          <Grid item xs={12} md={6} width="34%">
            <Card
              elevation={0}
              sx={{
                background: CARD_BG,
                borderRadius: 2,
                // border: `1px solid ${BORDER}`,
                boxShadow: `
      0 4px 16px 0 rgba(18, 122, 253, 0.07),   // colored brand shadow
      0 2px 8px 0 rgba(0,0,0,0.08)             // normal soft shadow
    `,
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  fontWeight={500}
                  sx={{
                    mb: 2,
                    color: TEXT,
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: "18px",
                    textShadow: "1px 1px 2px rgba(0,0,0,0.15)",
                  }}
                >
                  Job Errors by Environment
                </Typography>
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart data={jobErrorsByEnv} layout="vertical">
                    <defs>
                      {/* Drop shadow filter for chart bars */}
                      <filter id="chart-bar-shadow" height="130%">
                        <feDropShadow
                          dx="0"
                          dy="2"
                          stdDeviation="2"
                          floodColor="#127AFD"
                          floodOpacity="0.17"
                        />
                      </filter>
                    </defs>
                    <XAxis
                      type="number"
                      domain={[0, "dataMax"]}
                      tickFormatter={(value) => Math.floor(value)}
                      style={{
                        fontSize: 12,
                        fill: TEXT,
                        fontFamily:
                          '"Roboto", "Helvetica", "Arial", sans-serif',
                      }}
                    />
                    <YAxis
                      dataKey="label"
                      type="category"
                      width={100}
                      style={{
                        fontSize: 12,
                        fill: TEXT,
                        fontFamily:
                          '"Roboto", "Helvetica", "Arial", sans-serif',
                      }}
                    />
                    <Tooltip content={<SimpleTooltip />} />
                    <Bar
                      dataKey="count"
                      fill={BLUE_PALETTE[2]}
                      barSize={18}
                      // Here's the shadow filter on Bar elements:
                      filter="url(#chart-bar-shadow)"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Failure count based on Folders */}
          <Grid item xs={12} md={6} width="34%">
            <Card
              elevation={0}
              sx={{
                background: CARD_BG,
                borderRadius: 2,

                boxShadow: `
      0 4px 16px 0 rgba(18, 122, 253, 0.07),   // colored brand shadow
      0 2px 8px 0 rgba(0,0,0,0.08)             // normal soft shadow
    `,
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  fontWeight={500}
                  sx={{
                    mb: 2,
                    color: TEXT,
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: "18px",
                    textShadow: "1px 1px 2px rgba(0,0,0,0.15)",
                  }}
                >
                  Failure Count Based on Folders
                </Typography>
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart
                    data={errorTypes.sort((a, b) => b.count - a.count)}
                    layout="horizontal"
                  >
                    <XAxis
                      dataKey="label"
                      style={{
                        fontSize: 12,
                        fill: TEXT,
                        fontFamily:
                          '"Roboto", "Helvetica", "Arial", sans-serif',
                      }}
                    />
                    <YAxis
                      tickFormatter={(value) => Math.floor(value)}
                      style={{
                        fontSize: 12,
                        fill: TEXT,
                        fontFamily:
                          '"Roboto", "Helvetica", "Arial", sans-serif',
                      }}
                    />
                    <Tooltip content={<SimpleTooltip />} />
                    <Bar dataKey="count" fill={BLUE_PALETTE[4]} barSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Critical Jobs Table */}
          <Grid item xs={12} md={6} width="31%">
            <Card
              elevation={1}
              sx={{
                height: 234,
                background: CARD_BG,
                borderRadius: 2,
                // border: `1px solid ${BORDER}`,
                boxShadow: `
      0 4px 16px 0 rgba(18, 122, 253, 0.07),   // colored brand shadow
      0 2px 8px 0 rgba(0,0,0,0.08)             // normal soft shadow
    `,
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CardContent
                sx={{
                  p: 1,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={500}
                  sx={{
                    mb: 1,
                    color: TEXT,
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: "18px",
                    textShadow: "1px 1px 2px rgba(0,0,0,0.15)",
                  }}
                >
                  Critical Jobs
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    maxHeight: "165px",
                    width: "95%",
                    overflowY: "auto",
                    overflowX: "auto",
                    border: `1px solid ${BORDER}`,
                    borderRadius: 1,
                    background: "#fff",
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    "&::-webkit-scrollbar": {
                      height: "8px",
                      width: "8px",
                      background: "#E8F6FE",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#127AFD",
                      borderRadius: "8px",
                      minHeight: "24px",
                      border: "2px solid #E8F6FE",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#42C7F2",
                    },
                    scrollbarColor: "#127AFD #E8F6FE",
                    scrollbarWidth: "thin",
                  }}
                >
                  <Table
                    size="medium"
                    sx={{
                      minWidth: 500, // or your desired min width for scroll
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                      "& .MuiTableCell-root": {
                        padding: "4px 6px",
                        fontSize: "0.7rem",
                        lineHeight: "1",
                        borderBottom: `1px solid ${BORDER}`,
                        color: TEXT,
                        fontFamily:
                          '"Roboto", "Helvetica", "Arial", sans-serif',
                      },
                      "& .MuiTableHead-root": {
                        background: TABLE_ALT,
                        "& .MuiTableCell-root": {
                          fontWeight: 700,
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          background: TABLE_ALT,
                          fontFamily:
                            '"Roboto", "Helvetica", "Arial", sans-serif',
                        },
                      },
                      "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)":
                        {
                          background: BG_LIGHT,
                        },
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Criticality</TableCell>
                        <TableCell>Job Script</TableCell>
                        <TableCell>Occurrence Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {criticalJobs.length > 0 ? (
                        criticalJobs.map((job, index) => (
                          <TableRow key={index}>
                            <TableCell>{job.criticality}</TableCell>
                            <TableCell>{job.jobs_script}</TableCell>
                            <TableCell>{job.occurrence_count}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} align="left">
                            No critical jobs with errors found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} width="34%">
            <Card
              elevation={0}
              sx={{
                background: CARD_BG,
                borderRadius: 2,
                // border: `1px solid ${BORDER}`,
                boxShadow: "0 4px 20px rgba(18, 122, 253, 0.16)",
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  fontWeight={500}
                  sx={{
                    mb: 2,
                    color: TEXT,
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: "18px",
                    textShadow: "1px 1px 2px rgba(0,0,0,0.15)",
                  }}
                >
                  Job Criticality Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={170}>
                  <PieChart>
                    <Pie
                      data={jobCriticality}
                      dataKey="count"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      innerRadius={0}
                      outerRadius={70}
                      label={false}
                    >
                      {jobCriticality.map((entry, index) => (
                        <Cell
                          key={index}
                          fill={BLUE_PALETTE[(index + 1) % BLUE_PALETTE.length]}
                        />
                      ))}
                    </Pie>
                    <Legend
                      layout="vertical"
                      verticalAlign="middle"
                      align="right"
                      wrapperStyle={{
                        fontSize: "14px", // or your desired size
                        fontFamily:
                          '"Roboto", "Helvetica", "Arial", sans-serif',
                      }}
                    />

                    <Tooltip content={<SimpleTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Job Status Summary */}
          <Grid item xs={12} md={6} width="34%">
            <Card
              elevation={0}
              sx={{
                background: CARD_BG,
                borderRadius: 2,
                // border: `1px solid ${BORDER}`,
                boxShadow: `
      0 4px 16px 0 rgba(18, 122, 253, 0.07),   // colored brand shadow
      0 2px 8px 0 rgba(0,0,0,0.08)             // normal soft shadow
    `,
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  fontWeight={500}
                  sx={{
                    mb: 2,
                    color: TEXT,
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: "18px",
                    textShadow: "1px 1px 2px rgba(0,0,0,0.15)",
                  }}
                >
                  Job Status Summary
                </Typography>
                <ResponsiveContainer width="100%" height={170}>
                  <PieChart>
                    <Pie
                      data={jobStatus}
                      dataKey="count"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      innerRadius={0}
                      outerRadius={70}
                      label={false} // labels off to prevent overlap
                    >
                      {jobStatus.map((entry, index) => (
                        <Cell
                          key={index}
                          fill={BLUE_PALETTE[index % BLUE_PALETTE.length]}
                        />
                      ))}
                    </Pie>
                    <Legend
                      layout="vertical"
                      verticalAlign="middle"
                      align="right"
                      wrapperStyle={{
                        fontSize: "14px", // or your preferred size
                        fontFamily:
                          '"Roboto", "Helvetica", "Arial", sans-serif',
                      }}
                    />

                    <Tooltip content={<SimpleTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Job Criticality Distribution */}

          {/* Process Type Distribution */}
          <Grid item xs={12} md={6} width="31%">
            <Card
              elevation={0}
              sx={{
                background: CARD_BG,
                borderRadius: 2,
                // border: `1px solid ${BORDER}`,
                boxShadow: `
      0 4px 16px 0 rgba(18, 122, 253, 0.07),   // colored brand shadow
      0 2px 8px 0 rgba(0,0,0,0.08)             // normal soft shadow
    `,
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  fontWeight={500}
                  sx={{
                    mb: 2,
                    color: TEXT,
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: "18px",
                    textShadow: "1px 1px 2px rgba(0,0,0,0.15)",
                  }}
                >
                  Process Type Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={170}>
                  <PieChart>
                    <Pie
                      data={processTypes}
                      dataKey="count"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      innerRadius={18}
                      outerRadius={60}
                      label={false}
                    >
                      {processTypes.map((entry, index) => (
                        <Cell
                          key={index}
                          fill={BLUE_PALETTE[(index + 2) % BLUE_PALETTE.length]}
                        />
                      ))}
                    </Pie>
                    <Legend
                      verticalAlign="bottom"
                      height={34}
                      wrapperStyle={{
                        fontSize: "14px", // or your preferred size
                        fontFamily:
                          '"Roboto", "Helvetica", "Arial", sans-serif',
                      }}
                    />
                    <Tooltip content={<SimpleTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Weekly Error Count */}
          <Grid item xs={12} md={6} width="100%">
            <Card
              elevation={0}
              sx={{
                background: CARD_BG,
                borderRadius: 2,
                // border: `1px solid ${BORDER}`,
                boxShadow: `
      0 4px 16px 0 rgba(18, 122, 253, 0.07),   // colored brand shadow
      0 2px 8px 0 rgba(0,0,0,0.08)             // normal soft shadow
    `,
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  fontWeight={500}
                  sx={{
                    mb: 2,
                    color: TEXT,
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: "18px",
                    textShadow: "1px 1px 2px rgba(0,0,0,0.15)",
                  }}
                >
                  Weekly Error Count
                </Typography>
                <ResponsiveContainer width="100%" height={150}>
                  <AreaChart data={weeklyErrors}>
                    <defs>
                      <linearGradient
                        id="areaBlueMono"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor={BLUE_PALETTE[5]}
                          stopOpacity={0.7}
                        />
                        <stop
                          offset="100%"
                          stopColor={BG_LIGHT}
                          stopOpacity={0.3}
                        />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="week"
                      style={{
                        fontSize: 12,
                        fill: TEXT,
                        fontFamily:
                          '"Roboto", "Helvetica", "Arial", sans-serif',
                      }}
                    />
                    <YAxis
                      tickFormatter={(value) => Math.floor(value)}
                      style={{
                        fontSize: 12,
                        fill: TEXT,
                        fontFamily:
                          '"Roboto", "Helvetica", "Arial", sans-serif',
                      }}
                    />
                    <Tooltip content={<SimpleTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke={BLUE_PALETTE[1]}
                      strokeWidth={2}
                      fill="url(#areaBlueMono)"
                      dot={{
                        r: 4,
                        fill: BLUE_PALETTE[1],
                        stroke: "#fff",
                        strokeWidth: 2,
                      }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Home;
